/**
 * Another player's run, re-simulated here from their inputs: a second simulation of the course fed
 * the frames they send (`session.ts`), drawn as a ghost in the local player's viewport, and recorded
 * so the race is saved with their tape too. The simulation is deterministic, so with the same build
 * and tuning the ghost moves exactly as the player did on their own screen, a network delay behind.
 *
 * The frames arrive in bursts; the replica paces them one per local tick once a small buffer has
 * built up, so the ghost moves smoothly, and swallows a backlog beyond `MAX_BACKLOG` at once, so it
 * never drifts far behind. A gap in the stream (a missed message, a late join) asks the player for
 * their whole tape since their run started, which re-simulates in milliseconds.
 */

import { type RenderFeel, ViewState } from "@lache/render";
import {
  type CourseData,
  createInputFrame,
  decodeFrame,
  Ev,
  Sim,
  type TapeFrame,
  type Tuning,
} from "@lache/sim";
import { MAX_RECORDED_FRAMES, Recorder } from "../recorder";

/** Ticks buffered before the ghost starts moving after a stall, and the backlog it swallows at once. */
export const START_BUFFER = 6;
export const MAX_BACKLOG = 30;
/** Bound recovery work so one remote's long recording cannot monopolize a render frame. */
const MAX_TAPE_CATCH_UP = 2400;
const MAX_CATCH_UP = 240;

function validCounter(value: number): boolean {
  return Number.isSafeInteger(value) && value >= 0;
}

/** Peer JSON is untrusted, including counts such as 1e400 which JSON.parse turns into Infinity. */
function frameTicks(frames: TapeFrame[]): number | null {
  if (!Array.isArray(frames) || frames.length > MAX_RECORDED_FRAMES) return null;
  let ticks = 0;
  for (const f of frames) {
    if (!Array.isArray(f) || f.length !== 8 || !f.every(Number.isSafeInteger)) return null;
    if (
      f[0] < 1 ||
      f.slice(1, 5).some((axis) => axis < -127 || axis > 127) ||
      f[5] < 0 ||
      f[5] > 255 ||
      f[6] < 0 ||
      f[6] > 255 ||
      f[7] < 0 ||
      f[7] > 15
    )
      return null;
    ticks += f[0];
    if (!Number.isSafeInteger(ticks)) return null;
  }
  return ticks;
}

export class RemotePlayer {
  sim: Sim;
  readonly view = new ViewState();
  readonly recorder = new Recorder();
  private readonly input = createInputFrame();
  /** The player's run generation the replica follows (-1 before the first frame). */
  gen = -1;
  private queue: TapeFrame[] = [];
  private head = 0;
  private headLeft = 0;
  /** The last tick number queued and the one simulated. */
  private queuedThrough = 0;
  private started = false;
  /** Whether a gap was seen and a tape asked for; cleared when the tape lands. */
  needsTape = false;
  finish: { time: number; falls: number } | null = null;
  runSplits: number[] = [];
  runStartTick = 0;
  /** The hash the player reported for a tick, checked when the replica gets there. */
  private pendingHash: { tick: number; hash: string } | null = null;
  desynced = false;

  constructor(
    private courseName: string,
    course: CourseData,
    tuning: Tuning,
  ) {
    this.sim = new Sim(course, tuning);
  }

  get tick(): number {
    return this.sim.state.tick;
  }

  /** Ticks received and not yet simulated. */
  get backlog(): number {
    return this.queuedThrough - this.sim.state.tick;
  }

  setCourse(name: string, course: CourseData, tuning: Tuning): void {
    this.courseName = name;
    this.sim = new Sim(course, tuning);
    this.gen = -1;
    this.clear();
  }

  setTuning(tuning: Tuning): void {
    this.sim.setTuning(tuning);
  }

  private clear(): void {
    this.queue = [];
    this.head = 0;
    this.headLeft = 0;
    this.queuedThrough = 0;
    this.started = false;
    this.needsTape = false;
    this.finish = null;
    this.runSplits = [];
    this.runStartTick = 0;
    this.pendingHash = null;
    this.desynced = false;
  }

  /** A new run of the player (a restart, a course load): a fresh simulation from the course start. */
  reset(gen: number): void {
    this.gen = gen;
    this.sim.reset(0);
    this.sim.prev.copyFrom(this.sim.state);
    this.recorder.start(this.courseName, this.sim.tuning.hash, 0);
    this.clear();
  }

  /**
   * Frames for ticks `firstTick` onward. Returns false when they cannot be used (a gap in the stream,
   * or an older run): the caller then asks the player for their tape.
   */
  pushInputs(gen: number, firstTick: number, frames: TapeFrame[]): boolean {
    const ticks = frameTicks(frames);
    if (
      !validCounter(gen) ||
      !validCounter(firstTick) ||
      firstTick < 1 ||
      ticks === null ||
      !Number.isSafeInteger(firstTick - 1 + ticks)
    )
      return false;
    if (gen < this.gen) return true;
    if (gen > this.gen) this.reset(gen);
    if (firstTick > this.queuedThrough + 1) {
      this.needsTape = true;
      return false;
    }
    if (this.needsTape) return false;
    // Overlap with what is already queued (a resend): skip it.
    let skip = this.queuedThrough + 1 - firstTick;
    for (const f of frames) {
      let count = f[0];
      if (skip > 0) {
        const s = skip < count ? skip : count;
        skip -= s;
        count -= s;
        if (count === 0) continue;
      }
      const frame: TapeFrame = count === f[0] ? f : [count, f[1], f[2], f[3], f[4], f[5], f[6], f[7]];
      if (this.queue.length === 0 || this.head >= this.queue.length) this.headLeft = frame[0];
      this.queue.push(frame);
      this.queuedThrough += count;
    }
    if (this.head >= this.queue.length) this.headLeft = this.queue[this.head]?.[0] ?? 0;
    return true;
  }

  /** Replace the stream; long recordings finish catching up over later ticks. */
  loadTape(gen: number, frames: TapeFrame[]): boolean {
    const ticks = frameTicks(frames);
    if (!validCounter(gen) || gen < this.gen || ticks === null) return false;
    this.reset(gen);
    this.queue = frames.map((f) => f.slice() as TapeFrame);
    this.head = 0;
    this.headLeft = this.queue[0]?.[0] ?? 0;
    this.queuedThrough = ticks;
    this.needsTape = false;
    let n = 0;
    while (this.backlog > 0 && n++ < MAX_TAPE_CATCH_UP) this.step();
    this.sim.prev.copyFrom(this.sim.state);
    this.started = true;
    return true;
  }

  /** The player reported their hash at `tick`; a different one here means the two simulations disagree. */
  expectHash(gen: number, tick: number, hash: string): void {
    if (!validCounter(tick) || gen !== this.gen || tick <= this.sim.state.tick) return;
    this.pendingHash = { tick, hash };
  }

  setFinish(gen: number, time: number, falls: number, splits: (number | null)[]): boolean {
    if (
      !validCounter(gen) ||
      gen < this.gen ||
      !Number.isFinite(time) ||
      time <= 0 ||
      !validCounter(falls) ||
      !Array.isArray(splits) ||
      !splits.every((split) => split === null || (Number.isFinite(split) && split >= 0))
    )
      return false;
    if (gen !== this.gen && gen > this.gen) this.reset(gen);
    this.finish = { time, falls };
    for (let i = 0; i < splits.length; i++) {
      const s = splits[i];
      // Warm-up/missed checkpoints are holes locally and null after JSON serialization.
      if (s !== undefined && s !== null && this.runSplits[i] === undefined) this.runSplits[i] = s;
    }
    return true;
  }

  /** One local tick: the paced consumption of the stream. */
  pace(): void {
    const backlog = this.backlog;
    if (backlog <= 0) {
      this.started = false;
      return;
    }
    if (!this.started) {
      if (backlog < START_BUFFER) return;
      this.started = true;
    }
    this.step();
    // Far behind (a stall, a tab that was hidden): swallow the backlog down to a short lead.
    let extra = Math.min(this.backlog - MAX_BACKLOG, MAX_CATCH_UP - 1);
    while (extra-- > 0) this.step();
  }

  private step(): void {
    if (this.head >= this.queue.length) return;
    while (this.headLeft <= 0) {
      this.head++;
      if (this.head >= this.queue.length) return;
      this.headLeft = this.queue[this.head]![0];
    }
    decodeFrame(this.queue[this.head]!, this.input);
    this.headLeft--;
    this.sim.step(this.input);
    this.recorder.push(this.input);
    const ev = this.sim.events;
    for (let i = 0; i < ev.count; i++) {
      const type = ev.type[i]!;
      if (type === Ev.RunStarted) {
        this.runStartTick = ev.tick[i]!;
        this.runSplits = [];
      } else if (type === Ev.Checkpoint) this.runSplits[ev.a[i]!] = ev.b[i]!;
      else if (type === Ev.RunFinished && this.finish === null)
        this.finish = { time: ev.a[i]!, falls: ev.b[i]! };
    }
    ev.clear();
    const pending = this.pendingHash;
    if (pending && this.sim.state.tick >= pending.tick) {
      if (this.sim.state.tick === pending.tick && this.sim.hash() !== pending.hash) this.desynced = true;
      this.pendingHash = null;
    }
    // The consumed head of the queue is dropped now and then; the recorder keeps the whole run.
    if (this.head > 512) {
      this.queue.splice(0, this.head);
      this.head = 0;
    }
  }

  updateView(alpha: number, feel: RenderFeel, dt: number): void {
    this.view.update(this.sim.prev, this.sim.state, alpha, this.sim.tuning, feel, dt);
  }
}
